import React, { FC } from 'react'
import i18n from 'i18next'
import { ModalProps, ModalView } from '../../../components/organisms/Modal'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { crateEmailTemplatesFn, emailTemplatesFn, getRealmConfigurationFn, updateEmailTemplatesFn } from '../../../api'
import { useParams } from 'react-router-dom'
import { find } from 'lodash'
import { useUserContext } from '../../../contexts/UserContext'
import Loader from '../../../components/molecules/Loader'
import ReactSelect from '../../../components/molecules/ReactSelect'
import { BlackBoldParagraph } from '../../../components/atoms/Paragraph'
import { Switch } from '../../../components/molecules/Switch'
import { EmailTemplate, EmailTemplateTranslation } from '../../../@types'
import InputWithCaptions from '../../../components/molecules/InputWithCaptions'
import { TextArea } from '../../../components/atoms/Input'
import Tabs, { Tab } from '../../../components/molecules/Tabs'
import Button from '../../../components/molecules/Button'
import defaultActivationTemplate from './defaultActivationTemplate'
import { getLocales, Locale } from '../../../utils/locales'
import Tooltip from 'components/atoms/Tooltip'
import { Icon } from '@iconify/react'

const defaultLocaleItems = (locale: Locale) => ({
  language: locale.code,
  subject: i18n.getFixedT(locale.code)('modal.addEditEmailTemplate.defaultSubject'),
  body: defaultActivationTemplate,
})

const AddEditEmailTemplate: FC<ModalProps> = ({ onClose }) => {
  const [t] = useTranslation()
  const params = useParams<{ id: string }>()
  const email_template_id = +params.id!

  const [currentTab, setCurrentTab] = React.useState('0')

  const { state } = useUserContext()
  const { i_realm } = state?.realm

  const categories = ['Activation'].map((category) => ({
    label: t('modal.addEditEmailTemplate.categoryActivation'),
    value: category,
  }))
  const queryClient = useQueryClient()

  const locales = getLocales()

  const { data: selectedEmailTemplate } = useQuery(['email_templates', i_realm], emailTemplatesFn, {
    select: (data) => {
      const template = find(data, (i) => i.id === email_template_id)
      if (!template) {
        return {
          i_realm,
          category: 'Activation',
          name: '',
          default: false,
          translations: locales.map(defaultLocaleItems) as Partial<EmailTemplateTranslation>[],
        }
      }

      const translations = locales.map((locale) => {
        const localeTemplate = template.translations.find((t) => t.language === locale.code)
        if (localeTemplate) {
          return localeTemplate
        }
        return defaultLocaleItems(locale)
      })
      return {
        ...template,
        translations: translations,
      }
    },
  })

  const { data: selectedRealmConfiguration } = useQuery(['realm_configuration', i_realm], getRealmConfigurationFn, {
    select: (data) => data?.template_variables || {},
  })

  const { mutate: updateEmailTemplate } = useMutation((data: EmailTemplate) => updateEmailTemplatesFn(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('email_templates')
      onClose()
    },
  })

  const { mutate: createEmailTemplate } = useMutation((data: Partial<EmailTemplate>) => crateEmailTemplatesFn(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('email_templates')
      onClose()
    },
  })

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<EmailTemplate>({
    defaultValues: selectedEmailTemplate,
  })

  const { fields } = useFieldArray({
    control,
    name: 'translations', // Name of the field array
  })

  const onSubmit = (data: any) => {
    if (!data?.id) {
      createEmailTemplate(data)
    } else {
      updateEmailTemplate(data)
    }
  }

  const renderEmailTemplate = (index: string) => {
    const formField = fields[+index] || fields[0]

    return (
      <div key={formField.id + index}>
        <Controller
          // @ts-ignore Type '`translations[${string}].body`' is not assignable to type '"default"
          name={`translations[${index}].subject`}
          control={control}
          defaultValue={formField.subject}
          // @ts-ignore Type '`translations[${string}].body`' is not assignable to type '"default"
          render={({ field: { value, ...props } }) => (
            <InputWithCaptions type="text" caption={t('page.emails.subject')} value={value as string} {...props} />
          )}
        />
        <br />
        <Controller
          // @ts-ignore Type '`translations[${string}].body`' is not assignable to type '"default"
          name={`translations[${index}].body`}
          control={control}
          defaultValue={formField.body}
          // @ts-ignore Type '`translations[${string}].body`' is not assignable to type '"default"
          render={({ field: { ref, value, ...props } }) => <TextArea rows={20} value={value} {...props} />}
        />
      </div>
    )
  }

  const rosterVariables = [
    'firstname',
    'lastname',
    'title',
    'organization',
    'address_private',
    'tel_private',
    'tel_work_mobile',
    'tel_work',
    'email_work',
    'email_private',
    'gender',
    'birthdate',
    'language',
  ]

  const renderVariables = () => {
    return (
      <div>
        <h5 style={{ marginBottom: 8 }}>{t('modal.addEditEmailTemplate.availableVariables')}</h5>
        <ul style={{ marginTop: 8 }}>
          {rosterVariables.map((field) => (
          <li key={field}>
            <code>{`{{ roster.${field} }}`}</code>
          </li>
        ))}
          <li>
            <code>{`{{ qr_code }}`}</code>
          </li>
          <li>
            <code>{`{{ qr_data }}`}</code>
          </li>
          {Object.entries(selectedRealmConfiguration || {}).map(([key, value]) => (
            <li key={key}>
              <code>{`{{ ${key} }}: ${value}`}</code>
            </li>
          ))}
        </ul>
        <h5 style={{ marginBottom: 8 }}>{t('modal.addEditEmailTemplate.embeddingQrCode')}</h5>
        <div
          style={{
            paddingLeft: '30px',
            paddingBottom: "10px",
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div>
            {t('modal.addEditEmailTemplate.embedHintPrefix')} <strong>cid:qr_code.png</strong>{' '}
            {t('modal.addEditEmailTemplate.embedHintSuffix')}
          </div>
          <div>
            {`
          ${t('modal.addEditEmailTemplate.embedHintExample')}
          <img width="300" src="cid:qr_code.png" alt="QR Code" />`}
          </div>
        </div>
      </div>
    )
  }

  if (!selectedEmailTemplate || !selectedRealmConfiguration) {
    return <Loader height="30vh" />
  }

  return (
    <ModalView title={t('modal.addEditEmailTemplate.title')} onClose={onClose}>
      <Content>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <InnerContent>
            <Field>
              <Controller
                name="category"
                defaultValue={selectedEmailTemplate.category}
                control={control}
                rules={{ required: true }}
                render={({ field: { ref, onChange, value, ...props } }) => {
                  return (
                    <ReactSelect
                      value={categories.find((category) => category.value === value)}
                      onChange={({ value }) => {
                        onChange(value)
                      }}
                      isSearchable={false}
                      options={categories}
                      placeholder={t('placeholder.chooseFeatureGroup')}
                      {...props}
                    />
                  )
                }}
              />
              <div>
                <Controller
                  name="default"
                  control={control}
                  defaultValue={Boolean(selectedEmailTemplate.default)}
                  render={({ field: { value, onChange } }) => (
                    <div
                      style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-around',
                      }}
                    >
                      <BlackBoldParagraph text={t('realm.default_realm')} />
                      <Switch checked={value} onToggle={onChange} />
                    </div>
                  )}
                />
              </div>
              <Tooltip message={renderVariables()} width={700} position={'bottom'}>
                <Icon icon="uil:info-circle" style={{ fontSize: '24px' }} />
              </Tooltip>
            </Field>
            <Field>
              <Controller
                name="name"
                defaultValue={selectedEmailTemplate.name}
                control={control}
                rules={{ required: true }}
                render={({ field: { ref, ...props } }) => (
                  <InputWithCaptions type="text" caption={t('common.name') + '*'} {...props} />
                )}
              />
            </Field>
            <Tabs activeTab={String(currentTab)} onSwitch={setCurrentTab}>
              {fields.map((_, index) => (
                <Tab key={`email_template_${index}`} label={locales[index]?.name} value={String(index)} />
              ))}
            </Tabs>

            {renderEmailTemplate(currentTab)}
          </InnerContent>
          <SubmitButton appearance="blue" type="submit" disabled={!isValid}>
            {t('modal.addRealm.save')}
          </SubmitButton>
        </Form>
      </Content>
    </ModalView>
  )
}

const InnerContent = styled.div`
  overflow-y: scroll;
  height: 50vh;
  width: 100%;
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Form = styled.form`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
`
const Field = styled.div`
  display: flex;
  flex-flow: wrap;
  margin-bottom: 24px;
  gap: 12px;

  & > * {
    flex: 1;
  }
`

const SubmitButton = styled(Button)`
  margin-top: 32px;
  align-self: flex-end;
`

export default AddEditEmailTemplate
